import { useRouter, type UnknownOutputParams } from 'expo-router'
import { useMemo, type ReactNode } from 'react'
import { View } from 'react-native'

import { Typography } from '@/src/elements/text/Typography'
import { usePrayerSpaceV2 } from '@/src/features/prayer-space/services/usePrayerSpaceV2'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useTagsQuery } from '../../../../tag/services/useTagsQuery'
import { CategoryCard } from '../../components/CategoryCard'
import { CommunityCategories } from '../../components/CommunityCategories'
import { CommunitiesSlider } from './CommunitiesSliders'
import { CommunitySearch } from './CommunitySearch'
import { CommunitySearchParamsActions } from './CommunitySearchParamsActions'

type TCommunitiesScreenProps = {
  searchParams: UnknownOutputParams
}

export const CommunitiesScreen = ({ searchParams }: TCommunitiesScreenProps): ReactNode => {
  const { data: tags } = useTagsQuery()
  const { t } = useTranslate()
  const { navigate } = useRouter()
  const { routes } = useRoutes()
  const { lessThan } = useDimensions()

  const shouldShowNewPrayerSpace = usePrayerSpaceV2()?.shouldShowNewPrayerSpace ?? false

  const featuredTag = useMemo(() => tags?.find((tag) => tag.isFeatured), [tags])

  return (
    <View testID={TEST_ID.COMMUNITIES.SCREEN} className="mx-auto flex w-full max-w-[1400px] flex-col gap-4">
      <View className="gap-4 px-6">
        <CommunitySearchParamsActions searchParams={searchParams} />
        <CommunitySearch />

        {/* CATEGORIES */}
        {!shouldShowNewPrayerSpace && (
          <>
            <Typography size={lessThan.xm ? 'lg' : 'xl'} contrast="high" colors="default" className="mb-2 mt-8" bold>
              {t('communities.categories')}
            </Typography>
            <CommunityCategories />
          </>
        )}
      </View>

      {!shouldShowNewPrayerSpace && <CommunitiesSlider featuredTag={featuredTag} />}

      {/* ALL CATEGORIES */}
      {!shouldShowNewPrayerSpace && (
        <Typography size={lessThan.xm ? 'lg' : 'xl'} contrast="high" colors="default" className="mb-2 mt-8 px-6" bold>
          {t('communities.categories.all')}
        </Typography>
      )}
      <View className="flex-6 flex-row flex-wrap gap-6 px-6" testID={TEST_ID.COMMUNITIES.ALL_CATEGORIES}>
        {tags?.map((tag) => (
          <CategoryCard
            isPressable
            onPress={() => navigate(routes.communitiesCategory(tag.slug))}
            key={tag.id}
            imageSrc={getImageUrl(tag.icon)}
            content={tag.name}
            className="max-h-[231px] flex-1 grow"
          />
        ))}
      </View>
    </View>
  )
}
